"use client";

import { Clock, Hourglass, Wallet } from "lucide-react";
import { Link } from "react-router-dom";
import type { TrialUrgencyTier } from "@/lib/utils/trial";
import { formatCredits } from "@/features/subscription/hooks/use-credits";
import { useAppStore } from "@/stores";
import { DropdownMenu } from "@speakai/ui/dropdown-menu";
import { Avatar } from "@speakai/ui/avatar";
import { SidebarTooltip, trackSidebarEvent } from "@/components/layout/sidebar-primitives";
import { ProfileMenuContent } from "@/components/layout/profile-menu";
import { cn } from "@/lib/utils/cn";

interface SidebarFooterProps {
  billingPeriodLabel?: string | null;
  billEndDate: string;
  collapsed: boolean;
  changingLanguage: string | null;
  onSelectLanguage: (code: string) => void;
  onToggleCollapsed: () => void;
  planName: string;
  displayName: string;
  canManageBilling: boolean;
  creditMode: boolean;
  creditsRemaining: number;
  isApproachingLimit: boolean;
  isTrialActive: boolean;
  trialDaysLeft: number;
  trialTier: TrialUrgencyTier | null;
  onTrialSubscribe: () => void;
  planIsSubscription: boolean;
  profileMenuOpen: boolean;
  setProfileMenuOpen: (open: boolean) => void;
  routerPush: (href: string) => void;
  logout: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
  totalMinutes: number;
  usagePercent: number;
  usedMinutes: number;
  userAvatar?: string;
  userEmail?: string;
}

export function SidebarFooter({
  billingPeriodLabel,
  billEndDate,
  collapsed,
  changingLanguage,
  onSelectLanguage,
  onToggleCollapsed,
  planName,
  displayName,
  canManageBilling,
  creditMode,
  creditsRemaining,
  isApproachingLimit,
  isTrialActive,
  trialDaysLeft,
  trialTier,
  onTrialSubscribe,
  logout,
  planIsSubscription,
  profileMenuOpen,
  routerPush,
  setProfileMenuOpen,
  t,
  totalMinutes,
  usagePercent,
  usedMinutes,
  userAvatar,
  userEmail,
}: SidebarFooterProps) {
  const openSubscriptionGate = useAppStore((s) => s.openSubscriptionGate);

  const usedLabel = Math.round(usedMinutes).toLocaleString();
  const totalLabel = Math.round(totalMinutes).toLocaleString();
  const creditsLabel = t("SIDEBAR.CREDITS_LEFT", { credits: formatCredits(creditsRemaining), defaultValue: "{{credits}} left" });

  let planSummary: string | null = null;
  if (canManageBilling) {
    if (isTrialActive) {
      const trialLine = `${t("SIDEBAR.FREE_TRIAL")} · ${t("SIDEBAR.DAYS_LEFT", { count: trialDaysLeft })}`;
      planSummary = creditMode ? `${trialLine} · ${creditsLabel}` : trialLine;
    } else if (creditMode) {
      planSummary = `${planName} · ${creditsLabel}`;
    } else if (planIsSubscription) {
      planSummary = `${planName} · ${t("SIDEBAR.MENU.MINUTES", { used: usedLabel, total: totalLabel, defaultValue: "{{used}} / {{total}} min" })}`;
    } else {
      planSummary = planName || null;
    }
  }

  const trialTierClasses =
    trialTier === "red" ? "border-danger/40 bg-danger/10 text-danger"
      : trialTier === "amber" ? "border-warning/40 bg-warning/10 text-warning"
        : "border-primary/30 bg-primary/5 text-foreground";

  return (
    <div className="flex flex-col gap-2">
      {/* Expanded trial card */}
      {isTrialActive && canManageBilling && !collapsed && (
        <div className={`rounded-xl border px-3 py-3 ${trialTierClasses}`}>
          <p className="mb-2 text-sm font-semibold">
            {t("SIDEBAR.TRIAL.DAYS_LEFT", { count: trialDaysLeft, defaultValue: "{{count}} days left in trial" })}
          </p>
          {trialTier === "red" && (
            <p className="mb-2 text-xs">
              {t("SIDEBAR.TRIAL.RED_SUBLINE", { defaultValue: "Subscribe or add credit to keep your work" })}
            </p>
          )}
          <Link
            to="/pricing"
            onClick={onTrialSubscribe}
            aria-label={t("SIDEBAR.TRIAL.SUBSCRIBE_ARIA", { defaultValue: "Subscribe to a plan" })}
            className="inline-flex w-full items-center justify-center rounded-md border border-current px-3 py-1.5 text-xs font-medium transition-colors hover:bg-current/10 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            {t("SIDEBAR.TRIAL.SUBSCRIBE", { defaultValue: "Subscribe" })}
          </Link>
        </div>
      )}
      {/* Collapsed trial icon */}
      {isTrialActive && canManageBilling && collapsed && (
        <SidebarTooltip label={t("SIDEBAR.TRIAL.DAYS_LEFT", { count: trialDaysLeft, defaultValue: "{{count}} days left in trial" })} show>
          <Link
            to="/pricing"
            onClick={onTrialSubscribe}
            className={cn(
              "flex w-full items-center justify-center rounded-md p-1.5 hover:bg-primary/10 transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
              trialTier === "red" ? "text-danger" : trialTier === "amber" ? "text-warning" : "text-primary",
            )}
            aria-label={t("SIDEBAR.TRIAL.COLLAPSED_ARIA", { count: trialDaysLeft, defaultValue: "{{count}} days left in trial: subscribe" })}
          >
            <Hourglass className="h-4 w-4" aria-hidden="true" />
          </Link>
        </SidebarTooltip>
      )}
      {canManageBilling && planIsSubscription && creditMode && !collapsed && (
        <p className="px-1 text-xs text-muted-foreground truncate">
          {billEndDate
            ? t("SIDEBAR.CREDITS_COMPACT", {
              credits: formatCredits(creditsRemaining),
              date: billEndDate,
              defaultValue: "{{credits}} left · Renews {{date}}",
            })
            : t("SIDEBAR.CREDITS_COMPACT_NO_DATE", {
              credits: formatCredits(creditsRemaining),
              defaultValue: "{{credits}} left",
            })}
        </p>
      )}
      {canManageBilling && planIsSubscription && creditMode && collapsed && (
        <SidebarTooltip label={t("SIDEBAR.CREDITS_LEFT", { credits: formatCredits(creditsRemaining), defaultValue: "{{credits}} left" })} show>
          <button
            type="button"
            onClick={() => openSubscriptionGate({ source: "sidebar_subscription" })}
            className="flex w-full items-center justify-center rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            aria-label={t("SIDEBAR.CREDITS_LEFT", { credits: formatCredits(creditsRemaining), defaultValue: "{{credits}} left" })}
          >
            <Wallet className="h-4 w-4" />
          </button>
        </SidebarTooltip>
      )}
      {canManageBilling && planIsSubscription && !creditMode && !collapsed && usagePercent < 70 && (
        <p className="px-1 text-xs text-muted-foreground truncate">
          {billEndDate
            ? t("SIDEBAR.USAGE_COMPACT", {
              used: Math.round(usedMinutes).toLocaleString(),
              total: Math.round(totalMinutes).toLocaleString(),
              date: billEndDate,
            })
            : t("SIDEBAR.USAGE_COMPACT_NO_DATE", {
              used: Math.round(usedMinutes).toLocaleString(),
              total: Math.round(totalMinutes).toLocaleString(),
            })}
        </p>
      )}
      {canManageBilling && planIsSubscription && !creditMode && !collapsed && usagePercent >= 70 && (
        <div className="rounded-xl border border-border bg-muted px-3 py-3">
          <div className="mb-1.5 flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">
            <span>{t("SIDEBAR.USAGE")}</span>
            {billingPeriodLabel && <span className="normal-case font-semibold">{billingPeriodLabel}</span>}
          </div>
          <div className="mb-1.5 h-1.5 w-full overflow-hidden rounded-full bg-background">
            <div
              className={cn(
                "h-full rounded-full transition-all",
                isApproachingLimit ? "bg-warning" : "bg-primary",
              )}
              style={{ width: `${usagePercent}%` }}
            />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-xs text-foreground">
              <span className="font-semibold">{Math.round(usedMinutes).toLocaleString()}</span>
              <span className="text-muted-foreground"> {t("SIDEBAR.OF_MINUTES", { total: Math.round(totalMinutes).toLocaleString() })}</span>
            </p>
            {billEndDate && (
              <p className="text-[10px] text-muted-foreground">
                {t("SIDEBAR.RENEWS", { date: billEndDate })}
              </p>
            )}
          </div>
        </div>
      )}
      {canManageBilling && planIsSubscription && !creditMode && collapsed && (
        <SidebarTooltip label={`${Math.round(usedMinutes).toLocaleString()} of ${Math.round(totalMinutes).toLocaleString()} min used`} show>
          <button
            type="button"
            onClick={() => openSubscriptionGate({ source: "sidebar_subscription" })}
            className="flex w-full items-center justify-center rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-all focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            aria-label={`Usage: ${Math.round(usedMinutes).toLocaleString()} of ${Math.round(totalMinutes).toLocaleString()} minutes`}
          >
            <Clock className="h-4 w-4" />
          </button>
        </SidebarTooltip>
      )}

      <div className="border-t border-border pt-2">
        <div className={cn("flex items-center", collapsed ? "flex-col gap-1" : "gap-1")}>
          <div className={cn(!collapsed && "flex-1 min-w-0")}>
            <DropdownMenu
              open={profileMenuOpen}
              onOpenChange={setProfileMenuOpen}
              className={cn(!collapsed && "block w-full [&>div]:flex [&>div]:w-full [&>div]:min-w-0")}
              trigger={
                <span
                  className={cn(
                    "flex min-w-0 flex-1 items-center rounded-lg p-2 hover:bg-muted transition-colors",
                    collapsed ? "justify-center" : "gap-2.5",
                  )}
                >
                  <Avatar name={displayName} src={userAvatar} size="sm" imgReferrerPolicy="no-referrer" className={cn("h-7 w-7 shrink-0 ring-1 ring-border", userAvatar && "bg-white")} />
                  {!collapsed && (
                    <span className="ml-0.5 min-w-0 flex-1 text-left">
                      <span className="block truncate text-sm font-medium text-foreground">
                        {displayName}
                      </span>
                      {userEmail && (
                        <span className="block truncate text-[11px] text-muted-foreground">
                          {userEmail}
                        </span>
                      )}
                    </span>
                  )}
                  <span className="sr-only">User menu</span>
                </span>
              }
              align="left"
              side="top"
              width="w-64"
            >
              <ProfileMenuContent
                displayName={displayName}
                userAvatar={userAvatar}
                userEmail={userEmail}
                planSummary={planSummary}
                isTrialActive={isTrialActive}
                changingLanguage={changingLanguage}
                onSelectLanguage={onSelectLanguage}
                onNavigate={routerPush}
                onLogout={logout}
              />
            </DropdownMenu>
          </div>

          <SidebarTooltip label={collapsed ? t("SIDEBAR.OPEN_SIDEBAR") : t("SIDEBAR.CLOSE_SIDEBAR")} show>
            <button
              type="button"
              onClick={() => {
                trackSidebarEvent("Sidebar", collapsed ? "Expand" : "Collapse");
                onToggleCollapsed();
              }}
              className="hidden md:inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              aria-label={collapsed ? t("SIDEBAR.OPEN_SIDEBAR") : t("SIDEBAR.CLOSE_SIDEBAR")}
              aria-keyshortcuts="Meta+Backslash Control+Backslash"
              data-testid="sidebar-collapse-toggle"
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                <rect x="3" y="3" width="18" height="18" rx="3" />
                <path d="M9 3v18" />
              </svg>
            </button>
          </SidebarTooltip>
        </div>
      </div>
    </div>
  );
}
